import Vue from "vue";
import VueRouter from "vue-router";
import Layout from "../views/Layout.vue";
import HomeView from "../views/HomeView.vue";

Vue.use(VueRouter);

const routes = [
    {
        path: "/",
        name: "index",
        component: () => import("../views/Index.vue"),
        // NCNP 授权码回调的 redirect_uri 在国家平台注册为根路径，回调落此。
        // 用函数 redirect 保留 query 并分流：带 state 的是 NCNP 授权码回调 → 转交 /ncnp/callback
        // 完整处理（换 token + 回跳订购页 + task_id=pay 短路）；无 state 的 CA 单点登录回调与
        // 普通访问进 /portal（透传 query 供 permission.js 的 portal 分支走 ssoLogin）。
        redirect: (to) => {
            if (to.query && to.query.code && to.query.state) {
                return { path: "/ncnp/callback", query: to.query };
            }
            return { path: "/portal", query: to.query };
        },
        children: [
            {
                path: "portal",
                name: "portal",
                meta: {
                    title: "首页",
                    isUserAuthenticated: false, //是否需要登录 false 不需要登录 true 需要登录
                },
                component: () => import("../views/Portal.vue"),
            },
            {
                path: "compute",
                name: "compute",
                meta: {
                    title: "算力市场",
                    isUserAuthenticated: false,
                },
                component: () => import("../views/compute/Index.vue"),
            },
            {
                path: "ncnp/callback",
                name: "ncnpCallback",
                meta: {
                    title: "统一认证",
                    isUserAuthenticated: false,
                },
                component: () => import("../views/NcnpCallback.vue"),
            },
            {
                path: "ncnp",
                name: "ncnpEntry",
                meta: {
                    title: "国家平台入口",
                    isUserAuthenticated: false,
                },
                component: () => import("../views/ncnp/Entry.vue"),
            },
            {
                path: "orders/place",
                name: "ordersPlace",
                meta: {
                    title: "订购确认",
                    isUserAuthenticated: true,
                    // 外部入口页：未登录直达时自动发起 NCNP 授权码流程（而非落 CA）
                    ncnpAuthEntry: true,
                },
                component: () => import("../views/orders/Place.vue"),
            },
            {
                // 一期"详情=订购"：NCNP 上架的详情地址 /goods/detail 复用订购页组件
                path: "goods/detail",
                name: "goodsDetail",
                meta: {
                    title: "商品详情",
                    isUserAuthenticated: true,
                    ncnpAuthEntry: true,
                },
                component: () => import("../views/orders/Place.vue"),
            },
            {
                path: "orders/pay",
                name: "ordersPay",
                meta: {
                    title: "订单支付",
                    isUserAuthenticated: true,
                },
                component: () => import("../views/infra/Pay.vue"),
            },
            {
                path: "orders/detail",
                name: "ordersDetail",
                meta: {
                    title: "订单详情",
                    isUserAuthenticated: true,
                },
                component: () => import("../views/orders/Detail.vue"),
            },
            {
                path: "infra",
                name: "infra",
                meta: {
                    title: "我的订单",
                    isUserAuthenticated: true,
                },
                component: () => import("../views/infra/Index.vue"),
            },
            {
                path: "news",
                name: "news",
                meta: {
                    title: "新闻资讯",
                    isUserAuthenticated: false,
                },
                component: () => import("../views/news/Index.vue"),
            },
            {
                path: "news/detail/:id",
                name: "newsDetail",
                meta: {
                    title: "新闻资讯信息",
                    isUserAuthenticated: false,
                },
                component: () => import("../views/news/NewsDetail.vue"),
            },
            {
                path: "jsnews/detail/:id",
                name: "jsnewsDetail",
                meta: {
                    title: "新闻资讯信息",
                    isUserAuthenticated: false,
                },
                component: () => import("../views/local/NewsDetail.vue"),
            },
            {
                path: "platform",
                name: "platform",
                meta: {
                    title: "平台介绍",
                    isUserAuthenticated: false,
                },
                component: () => import("../views/platform/Index.vue"),
            },
            {
                path: "local/detail",
                name: "localDetail",
                meta: {
                    title: "地方平台介绍",
                    isUserAuthenticated: false,
                },
                component: () => import("../views/local/Detail.vue"),
            },
            {
                path: "local/jsdetail",
                name: "localJsDetail",
                meta: {
                    title: "地方平台介绍",
                    isUserAuthenticated: false,
                },
                component: () => import("../views/local/JiangsuDetail.vue"),
            },
        ],
    },

    {
        // NCNP "去使用"控制台链接中转入口（external_link_console 指向本路由）。
        // 顶层路由（不挂 / 父路由）避免带入 Index.vue 布局，保证中转无感。
        // 无本地 token 时守卫经 ncnpAuthEntry 走 NCNP 静默授权，换 token 后回本页；
        // 有 token 时组件 created 校验有效性后 window.location.replace 到智算一站式 AI 平台。
        path: "/console/transit",
        name: "consoleTransit",
        meta: {
            title: "跳转中",
            isUserAuthenticated: true,
            ncnpAuthEntry: true,
        },
        component: () => import("../views/ConsoleTransit.vue"),
    },

    {
        path: "/admin",
        component: Layout,
        children: [
            {
                path: "dashboard",
                name: "Dashboard",
                meta: {
                    title: "控制台",
                    isUserAuthenticated: true,
                },
                component: () => import("../views/HomeView.vue"),
            },
            {
                path: "system/user",
                name: "UserManagement",
                meta: {
                    title: "控制台",
                    isUserAuthenticated: true,
                },
                component: () => import("../views/system/User.vue"),
            },
            {
                path: "system/role",
                name: "RoleManagement",
                meta: {
                    title: "控制台",
                    isUserAuthenticated: true,
                },
                component: () => import("../views/system/Role.vue"),
            },
            {
                path: "infra/server",
                name: "ServerManagement",
                meta: {
                    title: "控制台",
                    isUserAuthenticated: true,
                },
                component: () => import("../views/infra/Server.vue"),
            },
            {
                path: "compute/resource",
                name: "ComputeResource",
                meta: {
                    title: "控制台",
                    isUserAuthenticated: true,
                },
                component: () => import("../views/compute/Resource.vue"),
            },
            {
                path: "system/consult",
                name: "ConsultManagement",
                meta: {
                    title: "控制台",
                    isUserAuthenticated: true,
                },
                component: () => import("../views/system/Consult.vue"),
            },
            {
                path: "system/news",
                name: "NewsManagement",
                meta: {
                    title: "控制台",
                    isUserAuthenticated: true,
                },
                component: () => import("../views/system/News.vue"),
            },
            {
                path: "system/platform",
                name: "PlatformManagement",
                meta: {
                    title: "控制台",
                    isUserAuthenticated: true,
                },
                component: () => import("../views/system/Platform.vue"),
            },
            {
                path: "system/local-platform",
                name: "LocalPlatformManagement",
                meta: {
                    title: "控制台",
                    isUserAuthenticated: true,
                },
                component: () => import("../views/system/LocalPlatform.vue"),
            },
        ],
    },
];

const router = new VueRouter({
    mode: "history",
    base: process.env.BASE_URL,
    routes,
    scrollBehavior(to, from, savedPosition) {
        return { x: 0, y: 0 };
    },
});

export default router;
